/**
 * AI Product Mapping — turn a backend column resolution into a display list.
 *
 * One entry per bound column, in logical-field order with a field's bindings
 * adjacent (ordered by column index), so every row carries its OWN provenance.
 * Grouping a multi-valued field into one row used to hide the second column's
 * source behind the first: "Product, Product Description" read as a single
 * User row even when the second column was LLM-proposed. Required fields the
 * server didn't map are surfaced as `missing` so they stand out; unmapped
 * optional fields are hidden. Confidence is only meaningful for LLM-proposed
 * bindings, so `conf` is left null for other sources; the raw binding `source`
 * is carried through so the view can label those rows.
 *
 * Shared by the run slice (the `detected` field) and the rail's columns_resolved
 * / columns_confirmed timeline events, so both render identical mappings.
 */

import { LOGICAL_FIELDS, REQUIRED_LOGICAL_FIELDS } from './constants';
import type { ColumnResolution } from '../api/types';
import type { MappedColumn } from './types';

export function mappedColumns(resolution: ColumnResolution | null | undefined): MappedColumn[] {
  const byField: Record<string, ColumnResolution['bindings']> = {};
  for (const b of resolution?.bindings || []) (byField[b.field] ||= []).push(b);
  const rows: MappedColumn[] = [];
  for (const { key, label } of LOGICAL_FIELDS) {
    const bound = [...(byField[key] || [])].sort((a, b) => a.columnIndex - b.columnIndex);
    const required = REQUIRED_LOGICAL_FIELDS.includes(key);
    if (bound.length === 0) {
      if (required) rows.push({ field: label, required, col: null, conf: null, missing: true });
      continue;
    }
    for (const b of bound) {
      rows.push({
        field: label,
        required,
        col: b.header || null,
        columnIndex: b.columnIndex,
        conf: b.source === 'llm' && typeof b.confidence === 'number' ? b.confidence : null,
        source: b.source ?? null,
      });
    }
  }
  return rows;
}
